import { t } from '../../shared/i18n.js';
import { el } from './dom.js';
import { icon } from './icons.js';
import { frame } from './onboarding.js';
import { SKIN_TONES, SHIRT_COLORS, TROUSER_COLORS } from '../game/character.js';

const KEY = 'abakwa-profile';
const AGE_KEY = 'abakwa-age';

// The name and look the player chose before, or null.
export function savedProfile() {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (p && typeof p.name === 'string' && p.name.trim()) return { name: p.name.slice(0, 20), look: { skin: p.look.skin | 0, shirt: p.look.shirt | 0, trousers: p.look.trousers | 0 }, start: p.start === 'here' ? 'here' : 'home' };
  } catch {
    // nothing usable saved
  }
  return null;
}

export function forgetProfile() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nothing to forget
  }
}

// The town is for adults. The year of birth is asked once and only 'adult' or 'minor' is kept.
export function ageKnown() {
  try {
    return localStorage.getItem(AGE_KEY);
  } catch {
    return null;
  }
}

export function askAge(root) {
  return new Promise((resolve) => {
    const known = ageKnown();
    if (known === 'adult') return resolve(true);
    const thisYear = new Date().getFullYear();
    const year = el('input', { type: 'number', id: 'born', min: 1900, max: thisYear, placeholder: t('For example 1998'), inputmode: 'numeric', autocomplete: 'bday-year' });
    const note = el('p.help', null, t('Abakwa is for adults. Asked once; only whether you are eighteen is kept, never the year.'));
    const closed = el('p.error', { role: 'alert', hidden: known !== 'minor' }, t('The town opens at eighteen. Come back then.'));
    const go = el('button.go', { type: 'submit', disabled: known === 'minor' }, t('Continue'));
    const form = el(
      'form',
      {
        onsubmit: (e) => {
          e.preventDefault();
          const y = Number(year.value);
          if (!(y >= 1900 && y <= thisYear)) {
            year.focus();
            return;
          }
          const adult = thisYear - y >= 18;
          try {
            localStorage.setItem(AGE_KEY, adult ? 'adult' : 'minor');
          } catch {
            // not remembered: asked again next time
          }
          if (!adult) {
            closed.hidden = false;
            year.disabled = true;
            go.disabled = true;
            return;
          }
          node.remove();
          resolve(true);
        },
      },
      el('label', { for: 'born' }, t('Your year of birth')),
      year,
      note,
      closed
    );
    if (known === 'minor') year.disabled = true;
    const node = frame({
      step: 1,
      title: t('Welcome to Bamenda'),
      lead: t('The real Bamenda, Buea and Limbe, alive with half a million people. Before you walk in, one question.'),
      children: form,
      footer: [go, el('a.rules', { href: '/rules.html', target: '_blank', rel: 'noopener' }, t('The rules of the town'))],
    });
    go.addEventListener('click', (e) => {
      e.preventDefault();
      form.requestSubmit();
    });
    root.append(node);
    year.focus();
  });
}

// The name, the look and where to start. Resolves with { name, look, start }.
export function askProfile(root, before) {
  return new Promise((resolve) => {
    const look = before ? { ...before.look } : { skin: Math.floor(Math.random() * SKIN_TONES.length), shirt: Math.floor(Math.random() * SHIRT_COLORS.length), trousers: Math.floor(Math.random() * TROUSER_COLORS.length) };
    const name = el('input', { type: 'text', id: 'who', maxlength: 20, autocomplete: 'nickname', placeholder: t('For example Che, Bih or Ngwa'), value: before ? before.name : '' });
    const figure = el('div.avatar', { 'aria-hidden': 'true' }, el('i.hair'), el('i.head'), el('i.shirt'), el('i.legs'), el('i.shadow'));
    const paint = () => {
      figure.querySelector('.head').style.background = SKIN_TONES[look.skin];
      figure.querySelector('.shirt').style.background = SHIRT_COLORS[look.shirt];
      figure.querySelector('.legs').style.background = TROUSER_COLORS[look.trousers];
    };
    const swatches = (label, part, colors) => {
      const group = el('div.swatches', { role: 'radiogroup', 'aria-label': label });
      colors.forEach((hex, k) => {
        const b = el('button', {
          type: 'button',
          role: 'radio',
          'aria-label': `${label} ${k + 1}`,
          'aria-checked': look[part] === k ? 'true' : 'false',
          style: `background:${hex}`,
          onclick: () => {
            look[part] = k;
            for (const o of group.children) o.setAttribute('aria-checked', o === b ? 'true' : 'false');
            paint();
          },
        }, icon('check', 16));
        group.append(b);
      });
      return el('div.field', null, el('span', null, label), group);
    };
    let start = before && before.start === 'here' ? 'here' : 'home';
    const startRow = el('div.field.start', { role: 'radiogroup', 'aria-label': t('Where you start') });
    const startButtons = [['home', t('At my house'), t('You wake up at your own door in your quarter.')], ['here', t('Where I really am'), t('Asks the browser for your location; works inside the North West or South West maps.')]].map(([id, label, text]) => {
      const b = el('button.choice', { type: 'button', role: 'radio', 'aria-checked': start === id ? 'true' : 'false', onclick: () => {
        start = id;
        for (const o of startButtons) o.setAttribute('aria-checked', o === b ? 'true' : 'false');
      } }, el('b', null, label), el('span', null, text));
      startRow.append(b);
      return b;
    });
    const error = el('p.error', { role: 'alert', hidden: true }, t('Type a name first, so people know what to call you.'));
    const go = el('button.go', { type: 'submit' }, t('Continue'));
    const form = el(
      'form',
      {
        onsubmit: (e) => {
          e.preventDefault();
          const who = name.value.replace(/\s+/g, ' ').trim();
          if (!who) {
            error.hidden = false;
            name.focus();
            return;
          }
          const profile = { name: who, look, start };
          try {
            localStorage.setItem(KEY, JSON.stringify(profile));
          } catch {
            // not remembered: they will be asked again next time
          }
          node.remove();
          resolve(profile);
        },
      },
      el('div.columns', null,
        el('div.fields', null, el('label', { for: 'who' }, t('Your name')), name, error, swatches(t('Skin'), 'skin', SKIN_TONES), swatches(t('Shirt'), 'shirt', SHIRT_COLORS), swatches(t('Trousers'), 'trousers', TROUSER_COLORS)),
        figure
      ),
      el('span.field-label', null, t('Where you start')),
      startRow
    );
    go.addEventListener('click', (e) => {
      e.preventDefault();
      form.requestSubmit();
    });
    const node = frame({
      step: 2,
      title: t('Who are you in this town?'),
      lead: t('A name people will call across the street, and the look you walk in with.'),
      children: form,
      footer: [go, el('p.help.terms', null, t('By entering you keep to '), el('a', { href: '/rules.html', target: '_blank', rel: 'noopener' }, 'the rules of the town'), t(': adults only, no contacts or slurs in the chat.'))],
    });
    paint();
    root.append(node);
    name.focus();
  });
}
